import { useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import { TransactionWarnings, formatBalance, formatDate } from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  setBlacklistData,
  setBlacklistDataVisible,
  setBlacklistDataItem,
  setBlacklistItemVisible,
  changePanelState,
} from '../store/slices/incoming-transaction';
import {
  useLazyGetProblemTransactionRecipientClientsOfficiasQuery,
  useCreateProblemTransactionChatMutation,
  useLazyGetCustomerIncomingTransactionWarningQuery,
} from '../store/services/customers';

const IncomingTransactionWarning = (): React.JSX.Element => {
  const {
    customersProblemTransactions: {
      incoming: { warnings, transaction, chats, recipientAccounts },
    },
    customersDictionaries: {
      customersTransactionTypes,
      currentEmployee,
      currenciesList,
    },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [getRecipientAccounts] =
    useLazyGetProblemTransactionRecipientClientsOfficiasQuery();

  const [createTransactionChat, { isLoading: chatCreateInProgress }] =
    useCreateProblemTransactionChatMutation();

  const [
    getIncomingTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetCustomerIncomingTransactionWarningQuery();

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { transactionId } = useParams();

  const getIncomingTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    await getIncomingTransactionWarningById({
      id,
      warningId,
    });
  };

  const createChatWithMessage = async ({
    message,
    userId,
  }: {
    message: string;
    userId?: string;
  }) => {
    const title = `RFI: Incoming transaction ${formatBalance(
      transaction?.sum || 0,
      currenciesList?.find((item) => item.id === transaction?.currencyId),
    )} ${transaction?.currency?.name} ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    const body: {
      title: string;
      employeeId: string;
      organizationId: string;
      userId?: string;
    } = {
      title,
      employeeId: currentEmployee?.id as string,
      organizationId: transaction?.recipientAccount?.organizationId,
    };

    if (userId) {
      body.userId = userId;
    }

    const response = (await createTransactionChat({
      id: transactionId as string,
      body,
    })) as { data: any };

    if (response && response.data && sendMessage) {
      sendMessage(
        JSON.stringify({
          event: 'subscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'message',
          data: {
            channelId: response?.data?.data?.id,
            content: message,
          },
        }),
      );

      sendMessage(
        JSON.stringify({
          event: 'unsubscribe',
          data: { channelId: response?.data?.data?.id },
        }),
      );
    }
  };

  useEffect(() => {
    if (
      transaction &&
      transaction.recipientAccount &&
      transaction.recipientAccount?.organization?.type === 'CORPORATE' &&
      !recipientAccounts
    ) {
      getRecipientAccounts(transaction.recipientAccount.organizationId);
    }
  }, [transaction, recipientAccounts]);

  return (
    <TransactionWarnings
      warnings={warnings}
      employee={undefined}
      transactionsTypes={customersTransactionTypes}
      setBlacklistDataVisible={(visible) => {
        dispatch(setBlacklistDataVisible(visible));
        dispatch(changePanelState({ state: false }));
      }}
      setBlacklistData={(data) => dispatch(setBlacklistData(data))}
      setBlacklistItemVisible={(visible) => {
        dispatch(setBlacklistItemVisible(visible));
        dispatch(changePanelState({ state: false }));
      }}
      setBlacklistDataItem={(data) => dispatch(setBlacklistDataItem(data))}
      transactionUrlType="incoming"
      transactionId={transactionId}
      companyOfficials={recipientAccounts}
      individualCustomer={transaction?.recipientAccount}
      chats={chats}
      createChatWithMessage={createChatWithMessage}
      chatCreateInProgress={chatCreateInProgress}
      getWarningById={getIncomingTransactionWarningByIdAction}
      isWarningByIdLoading={isWarningByIdLoading}
      isWarningByIdFetching={isWarningByIdFetching}
      withTransactionUrl
    />
  );
};

export default IncomingTransactionWarning;
